import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FaArrowLeft, FaCopy } from 'react-icons/fa';
//
import {
  getOrderList,
  getOrderBillAddr,
  getOrderShipAddr,
  getPayMethod,
  getShipMethod,
  getOrderItemDetails,
} from '../../api/services/orderService';
import { copySO } from '../../api/services/orderService';
import { getCartList } from '../../api/services/cartService';
import { getPaymentTerm } from '../../api/services/paymentService';
import { setUserCartList } from '../../redux/userCart';
import { setShowLoading } from '../../redux/systemInfo';
import { useDispatch } from 'react-redux';
//
import Layout from '../../components/Layout';

const Container = styled.div`
  flex: 1;
  padding: 20px 5%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ddd;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.primaryBlue};
  cursor: pointer;
  font-size: 16px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${(props) => props.theme.colors.primaryBlue};
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: ${(props) => props.theme.colors.primaryBlue};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #dc3545;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
`;

const PageTitle = styled.h1`
  font-size: 2.2rem;
  margin: 2rem 0 2.5rem 0;
  font-weight: 700;
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  color: #223a7a;
  font-weight: 700;
  text-transform: uppercase;
  border-bottom: 3px solid #223a7a;
  padding-bottom: 0.3rem;
  margin-bottom: 1.2rem;
  margin-top: 1.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  margin-bottom: 0.5rem;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBlock = styled.div`
  margin-bottom: 0.5rem;
`;

const AddressBlock = styled.div`
  line-height: 1.5;
  font-size: 1rem;
  color: ${(props) => props.theme.colors.primaryBlue};
  margin-bottom: 1.5rem;
`;

const ItemsSection = styled.div`
  margin-top: 2.5rem;
`;

const ItemTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const ItemTableHead = styled.thead``;

const ItemTableHeader = styled.th`
  padding: 12px 8px;
  text-align: left;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primaryBlue};
  border-bottom: 2px solid ${(props) => props.theme.colors.primaryBlue};
  white-space: nowrap;
`;

const ItemTableBody = styled.tbody``;

const ItemRow = styled.tr`
  border-bottom: 1px solid #eee;
  &:hover {
    background: #f9f9f9;
  }
`;

const ItemCell = styled.td`
  padding: 12px 8px;
  vertical-align: middle;
  font-size: 0.85rem;
  color: ${(props) => props.theme.colors.primaryBlue};
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  border: 1px solid #eee;
  border-radius: 4px;
`;

const TotalsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 1.1rem;
  font-size: 1.1rem;
  padding-right: 1.5rem;

  @media (max-width: 768px) {
    padding-right: 30px;
  }

  @media (max-width: 480px) {
    padding-right: 20px;
  }
`;

const TotalsRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.1rem;
  min-width: 200px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primaryBlue};
  padding: 4px 0;

  div:last-child {
    text-align: right;
    min-width: 80px;
  }

  @media (max-width: 768px) {
    min-width: 180px;
    gap: 0.2rem;
    font-size: 1rem;
  }
  @media (max-width: 480px) {
    min-width: 150px;
    gap: 0.1rem;
    font-size: 0.9rem;
  }
`;

const InfoValue = styled.div`
  font-weight: 400;
  color: ${(props) => props.theme.colors.primaryBlue};
  font-size: 0.95rem;
  white-space: pre-line;
  line-height: 1.3;
`;

export default function OrderDetailPage() {
  const { orderNo } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [shipmentMethod, setShipmentMethod] = useState(null);
  const [paymentTerm, setPaymentTerm] = useState(null);
  const [billingAddress, setBillingAddress] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [orderItems, setOrderItems] = useState([]);

  const getPaymentMethod = async () => {
    try {
      const result = await getPayMethod(orderNo);
      setPaymentMethod(result);
    } catch (err) {
      console.error('Error fetching payment method:', err);
      setPaymentMethod(null);
    }
  };

  const getShipmentMethod = async () => {
    try {
      const result = await getShipMethod(orderNo);
      setShipmentMethod(result);
    } catch (err) {
      console.error('Error fetching ship method:', err);
      setShipmentMethod(null);
    }
  };

  const getPaymentTermInfo = async () => {
    try {
      const result = await getPaymentTerm(userInfo.idCustomer);
      setPaymentTerm(result.payment_term);
    } catch (err) {
      console.error('Error fetching payment term:', err);
      setPaymentTerm(null);
    }
  };

  const getBillingAddress = async () => {
    try {
      const result = await getOrderBillAddr(orderNo, userInfo.idCustomer);
      setBillingAddress(result[0]);
    } catch (err) {
      console.error('Error fetching billing address:', err);
      setBillingAddress(null);
    }
  };

  const getShippingAddress = async () => {
    try {
      const result = await getOrderShipAddr(orderNo, userInfo.idCustomer);
      setShippingAddress(result[0]);
    } catch (err) {
      console.error('Error fetching shipping address:', err);
      setShippingAddress(null);
    }
  };

  const getOrderItems = async () => {
    try {
      const result = await getOrderItemDetails(orderNo);

      // The result is an array directly, not an object with orderItemList
      if (Array.isArray(result)) {
        setOrderItems(result);
      } else {
        console.error(
          'Failed to fetch order items - unexpected response format',
        );
        setOrderItems([]);
      }
    } catch (err) {
      console.error('Error fetching order items:', err);
      setOrderItems([]);
    }
  };

  useEffect(() => {
    const getOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get all orders from customer ID
        const allOrders = await getOrderList(userInfo.idCustomer, {
          from: '',
          to: '',
          orderNo: '',
          sku: '',
          address: '',
        });

        // Extract the specific order from allOrders
        const specificOrder = allOrders.orderList.find(
          (order) => order.idSO === orderNo,
        );

        if (specificOrder) {
          setOrder(specificOrder);
          await Promise.all([
            getBillingAddress(),
            getShippingAddress(),
            getPaymentMethod(),
            getShipmentMethod(),
            getPaymentTermInfo(),
            getOrderItems(),
          ]);
        } else {
          setError('Order not found');
        }
      } catch (err) {
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    if (userInfo.idCustomer) {
      getOrderDetails();
    }
  }, [orderNo, userInfo.idCustomer]);

  const handleCopyOrder = async () => {
    try {
      const result = await copySO(userInfo.idCustomer, orderNo);
      if (result.is_success) {
        // Update cart list
        if (userInfo.zipcode) {
          const cartResult = await getCartList(
            userInfo.idCustomer,
            userInfo.zipcode,
            userInfo.idStore,
          );
          dispatch(setUserCartList({ cart_list: cartResult.cartList }));
        }
        navigate(`/checkout?from=${orderNo}`);
      } else {
        alert('Failed to copy order');
      }
    } catch (err) {
      alert('Error copying order');
      console.error('Error copying order:', err);
    }
  };

  const getPaymentStatusText = (paidStatus) => {
    switch (paidStatus) {
      case '0':
        return 'UNPAID';
      case '1':
        return 'PARTIALLY PAID';
      case '2':
        return 'FULLY PAID';
      default:
        return 'UNKNOWN';
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <LoadingMessage>Loading order details...</LoadingMessage>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container>
          <ErrorMessage>{error}</ErrorMessage>
        </Container>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <Container>
          <ErrorMessage>Order not found</ErrorMessage>
        </Container>
      </Layout>
    );
  }

  // Placeholder data for missing fields
  const shippingMethod = order.shippingMethod ?? 'null';
  const shipping = order.shippingAddress || null;

  return (
    <Layout>
      <Container>
        <Header>
          <BackButton onClick={() => navigate('/myaccount/orders')}>
            <FaArrowLeft />
            Back to Orders
          </BackButton>
          <ActionButton onClick={handleCopyOrder}>
            <FaCopy />
            Copy Order
          </ActionButton>
        </Header>

        <PageTitle>Orders</PageTitle>
        <Grid>
          <div>
            <SectionTitle>Order Details</SectionTitle>
            <InfoBlock>
              <InfoRow>
                <InfoLabel>Order#: {order.idSO ?? ''}</InfoLabel>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Cust. PO#: {order.custPO ?? ''}</InfoLabel>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Date Added: {order.orderDT ?? ''}</InfoLabel>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Order Status: {order.status ?? ''}</InfoLabel>
              </InfoRow>
            </InfoBlock>
          </div>
          <div>
            <SectionTitle>Payment / Shipment</SectionTitle>
            <InfoBlock>
              <InfoRow>
                <InfoLabel>
                  Payment Status: {getPaymentStatusText(order.paidStatus)}
                </InfoLabel>
              </InfoRow>
              <InfoRow>
                <InfoLabel>
                  Payment Method:{' '}
                  {paymentMethod ? paymentMethod.paymentTypeName : ''}
                </InfoLabel>
              </InfoRow>
              <InfoRow>
                <InfoLabel>
                  Shipping Method:{' '}
                  {shipmentMethod ? shipmentMethod.shipViaName : ''}
                </InfoLabel>
              </InfoRow>
              <InfoRow>
                <InfoLabel>
                  Payment Term: {paymentTerm ? paymentTerm.paymentTermName : ''}
                </InfoLabel>
              </InfoRow>
            </InfoBlock>
          </div>
        </Grid>
        <Grid>
          <div>
            <SectionTitle>Sold To</SectionTitle>
            <AddressBlock>
              {billingAddress ? (
                <>
                  {billingAddress.firstNm && billingAddress.lastNm
                    ? `${billingAddress.firstNm} ${billingAddress.lastNm}`
                    : billingAddress.firstNm || billingAddress.lastNm || ''}
                  {(billingAddress.firstNm || billingAddress.lastNm) && <br />}
                  {billingAddress.addr || ''}
                  {billingAddress.addr2 && (
                    <>
                      <br />
                      {billingAddress.addr2}
                    </>
                  )}
                  <br />
                  {billingAddress.city || ''},{billingAddress.states || ''}
                  {billingAddress.zipcode || ''}
                  <br />
                  {billingAddress.country || ''}
                  <br />
                  {billingAddress.tel || ''}
                </>
              ) : (
                '[ Order Sold To ]'
              )}
            </AddressBlock>
          </div>
          <div>
            <SectionTitle>Ship To</SectionTitle>
            <AddressBlock>
              {shippingAddress ? (
                <>
                  {shippingAddress.firstNm && shippingAddress.lastNm
                    ? `${shippingAddress.firstNm} ${shippingAddress.lastNm}`
                    : shippingAddress.firstNm || shippingAddress.lastNm || ''}
                  {(shippingAddress.firstNm || shippingAddress.lastNm) && (
                    <br />
                  )}
                  {shippingAddress.addr || ''}
                  {shippingAddress.addr2 && (
                    <>
                      <br />
                      {shippingAddress.addr2}
                    </>
                  )}
                  <br />
                  {shippingAddress.city || ''}, {shippingAddress.states || ''}
                  {shippingAddress.zipcode || ''}
                  <br />
                  {shippingAddress.country || ''}
                  <br />
                  {shippingAddress.tel || ''}
                </>
              ) : (
                '[ Order Ship To ]'
              )}
            </AddressBlock>
          </div>
        </Grid>
        <SectionTitle>Ordered Item(s)</SectionTitle>
        <ItemsSection>
          {orderItems && orderItems.length > 0 ? (
            <ItemTable>
              <ItemTableHead>
                <tr>
                  <ItemTableHeader>Image</ItemTableHeader>
                  <ItemTableHeader>Item No</ItemTableHeader>
                  <ItemTableHeader>Barcode</ItemTableHeader>
                  <ItemTableHeader>Description</ItemTableHeader>
                  <ItemTableHeader>UoM</ItemTableHeader>
                  <ItemTableHeader>Qty</ItemTableHeader>
                  <ItemTableHeader>Unit Price</ItemTableHeader>
                  <ItemTableHeader>Total</ItemTableHeader>
                  <ItemTableHeader>SRP</ItemTableHeader>
                </tr>
              </ItemTableHead>
              <ItemTableBody>
                {orderItems.map((item, idx) => (
                  <ItemRow key={idx}>
                    <ItemCell>
                      <ItemImage
                        src={`${process.env.REACT_APP_FILE_UPLOAD_URL}${item.filepath1}`}
                        alt={item.idStyle || ''}
                      />
                    </ItemCell>
                    <ItemCell>{item.idStyle || '-'}</ItemCell>
                    <ItemCell>{item.barcode || '-'}</ItemCell>
                    <ItemCell>{item.webTitle || '-'}</ItemCell>
                    <ItemCell>
                      {item.qtyPerUOM && item.qtyPerCTN
                        ? `${item.qtyPerUOM}/${item.qtyPerCTN} (Pack/CTN)`
                        : '-'}
                    </ItemCell>
                    <ItemCell>{item.totQty || ''}</ItemCell>
                    <ItemCell>
                      {item.costPrice !== null && item.costPrice !== undefined
                        ? `$${parseFloat(item.costPrice).toFixed(2)}`
                        : ''}
                    </ItemCell>
                    <ItemCell>
                      {item.totPrice !== null && item.totPrice !== undefined
                        ? `$${parseFloat(item.totPrice).toFixed(2)}`
                        : ''}
                    </ItemCell>
                    <ItemCell>
                      {item.srp !== null && item.srp !== undefined
                        ? `$${item.srp}`
                        : '-'}
                    </ItemCell>
                  </ItemRow>
                ))}
              </ItemTableBody>
            </ItemTable>
          ) : (
            <div
              style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#666',
              }}
            >
              No items found for this order
            </div>
          )}
        </ItemsSection>
        <TotalsSection>
          <TotalsRow>
            <div>Subtotal: </div>
            <div>
              {order.grandAmount !== null && order.grandAmount !== undefined
                ? `$${parseFloat(order.grandAmount).toFixed(2)}`
                : ''}
            </div>
          </TotalsRow>
          <TotalsRow>
            <div>Discount: </div>
            <div>
              {order.discount !== null && order.discount !== undefined
                ? `$${parseFloat(order.discount).toFixed(2)}`
                : '-$0.00'}
            </div>
          </TotalsRow>
        </TotalsSection>
      </Container>
    </Layout>
  );
}
